import type { Locale } from "@ec2/i18n";
import { locales } from "@ec2/i18n";

/**
 * Only a language the player CHOSE (profile switch) is stored. v2 since
 * 2026-10-21: the first version also stored the account's default "en" that
 * the app adopted on every fresh session (SessionEffects), which pinned German
 * phones to English for good — a new key lets those devices follow their
 * device language again.
 */
const STORAGE_KEY = "ec2.locale.v2";

/** Best-effort match of a BCP47 tag onto our supported locales. */
export function matchLocale(tag: string | undefined | null): Locale | null {
  if (!tag) return null;
  const lang = tag.toLowerCase().split("-")[0];
  return (locales as readonly string[]).includes(lang ?? "") ? (lang as Locale) : null;
}

/** Initial locale: stored preference → browser language → English. */
export function detectLocale(
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
  navigatorLanguages: readonly string[] = typeof navigator !== "undefined" ? (navigator.languages ?? [navigator.language]) : [],
): Locale {
  const stored = matchLocale(storage?.getItem(STORAGE_KEY));
  if (stored) return stored;
  for (const tag of navigatorLanguages) {
    const match = matchLocale(tag);
    if (match) return match;
  }
  return "en";
}

export function persistLocale(locale: Locale, storage: Pick<Storage, "setItem"> | null = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, locale);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
